import { useState, useEffect } from "react";
import { addDays } from "date-fns";

type DayAvailability = "available" | "partial" | "unavailable";

interface UseCalendarAvailabilityReturn {
  getDateAvailability: (date: Date) => DayAvailability | undefined;
  isLoading: boolean;
  error: string | null;
}

export const useCalendarAvailability = (
  venueId: string | null,
  customerTypeId: string | null,
  participants?: number | null,
  timeSlotStart?: string | null,
  timeSlotEnd?: string | null,
  userRoleId: string | null = '5', // Default to GUEST_USER role
  enabled: boolean = true
): UseCalendarAvailabilityReturn => {
  const [availabilityData, setAvailabilityData] = useState<
    Record<string, DayAvailability>
  >({});
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // When explicitly disabled or missing required params, reset state and skip fetching
    if (!enabled || !venueId || !customerTypeId) {
      setAvailabilityData({});
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();

    const fetchAvailability = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          venue_id: venueId,
          customer_type_id: customerTypeId,
          start_date: new Date().toISOString().split("T")[0],
          end_date: addDays(new Date(), 365).toISOString().split("T")[0],
          user_role_id: userRoleId || "5", // Always pass user_role_id
          ...(participants && { participants: participants.toString() }),
          ...(timeSlotStart && { timeSlotStart }),
          ...(timeSlotEnd && { timeSlotEnd }),
        });

        console.log("🔄 useCalendarAvailability: Fetching availability for", {
          venueId,
          customerTypeId,
          participants,
          timeSlotStart,
          timeSlotEnd,
        });

        const response = await fetch(`/api/bookings/calendar-availability?${params.toString()}`, {
          signal: controller.signal
        });
        const data = await response.json();

        if (data.success) {
          // Convert API response to simple date->status map
          const availabilityMap: Record<string, DayAvailability> = {};

          // Check if API returned empty data (no time slots found)
          if (data.data && data.data.length === 0) {
            // No time slots found - mark all dates as unavailable
            console.log(
              "⚠️ useCalendarAvailability: No time slots found, marking all dates as unavailable",
            );

            // Generate unavailable status for next 365 days
            for (let i = 0; i < 365; i++) {
              const date = addDays(new Date(), i);
              const dateStr = date.toISOString().split("T")[0];
              availabilityMap[dateStr] = "unavailable";
            }
          } else {
            // Normal case - process the returned data
            data.data.forEach((item: any) => {
              availabilityMap[item.date] = item.status;
            });
          }

          setAvailabilityData(availabilityMap);

          console.log(
            "✅ useCalendarAvailability: Loaded",
            Object.keys(availabilityMap).length,
            "days",
          );
        } else {
          console.error("❌ useCalendarAvailability: API error:", data.error);
          setError(data.error || "Failed to load availability");
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Ignore aborted requests
          return;
        }
        console.error('❌ useCalendarAvailability: Fetch error:', err);
        setError('Failed to load calendar availability');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchAvailability();
    return () => {
      // Abort any in-flight request when params change
      controller.abort();
    };
  }, [venueId, customerTypeId, participants, timeSlotStart, timeSlotEnd, userRoleId, enabled]);

  const getDateAvailability = (date: Date): DayAvailability | undefined => {
    // Create local date string (avoid UTC timezone mismatch)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    return availabilityData[dateStr];
  };

  return { getDateAvailability, isLoading, error };
};
